import { useTranslation } from "react-i18next";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { trackDownload, trackCTAClick } from "@/lib/analytics";
import JsonLd from "@/components/JsonLd";
import { collectionPageSchema, breadcrumbSchema } from "@/lib/seo-schemas";
import PrintableGallery from "@/components/PrintableGallery";
import { printablesByCategory } from "@/data/printables";
import FaqGroupsSection, { toFaqGroup } from "@/components/FaqGroupsSection";

const AnimatedSection = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => {
  const [ref, isVisible] = useScrollAnimation<HTMLElement>();
  return (
    <section
      ref={ref}
      className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
    >
      {children}
    </section>
  );
};

const Activities = () => {
  const { t } = useTranslation();
  const activitiesGroups = (["resources"] as const)
    .map((k) => toFaqGroup(k, t(`faq.groups.${k}`, { returnObjects: true })))
    .filter(Boolean) as NonNullable<ReturnType<typeof toFaqGroup>>[];

  return (
    <>
      <PageHead
        title={t("activities.seoTitle")}
        description={t("activities.seoDesc")}
        canonical="https://queebi.com/activities"
        image="/images/og/activities-1200x630.jpg"
        imageAlt="Queebi play-based activities and free printables for kids"
        keywords="Queebi activities, printable emotion cards, children coloring pages, emotional learning activities, preschool worksheet, feelings chart for kids, SEL printables, social emotional learning tools"
      />
      <JsonLd
        data={[
          collectionPageSchema({
            name: "Queebi Activities & Downloads",
            description: "Free printable emotional learning tools for children ages 3–8: emotion cards, coloring pages, and play-based activities.",
            url: "https://queebi.com/activities",
          }),
          breadcrumbSchema([
            { name: "Home", url: "https://queebi.com/" },
            { name: "Activities", url: "https://queebi.com/activities" },
          ]),
        ]}
      />

      <section className="py-20 md:py-32 bg-cover bg-center" style={{ backgroundImage: `url(/images/brand/bg-pattern.png)` }}>
        <div className="container text-center max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold font-heading mb-8">{t("activities.title")}</h1>
          <p className="text-lg text-muted-foreground mb-4">{t("activities.subtitle")}</p>
          <p className="text-muted-foreground text-lg">{t("activities.handsOn")}</p>
        </div>
      </section>

      <AnimatedSection className="bg-card py-20 md:py-32">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-heading mb-6">{t("activities.printPlayTitle")}</h2>
            <p className="text-muted-foreground text-lg">{t("activities.printPlayDesc")}</p>
          </div>
          <PrintableGallery items={printablesByCategory("activity")} />
        </div>
      </AnimatedSection>


      <AnimatedSection className="bg-card py-20 md:py-32">
        <div className="container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl md:text-3xl font-bold font-heading mb-4">{t("activities.coloringTitle")}</h2>
            <p className="text-muted-foreground text-lg mb-2">{t("activities.coloringDesc")}</p>
            <p className="text-muted-foreground">{t("activities.coloringMore")}</p>
          </div>
          <PrintableGallery items={printablesByCategory("coloring")} />
        </div>
      </AnimatedSection>

      <AnimatedSection className="bg-background py-20 md:py-32">
        <div className="container flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1">
            <img src="/images/pages/mothers-day-preview.jpg" alt="Queebi Mother's Day coloring card preview" className="w-full max-w-lg mx-auto rounded-xl shadow-lg" width={600} height={600} loading="lazy" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl md:text-3xl font-bold font-heading mb-6">{t("activities.mothersDayTitle")}</h2>
            <p className="text-muted-foreground text-lg mb-8">{t("activities.mothersDayDesc")}</p>
            <Button asChild className="rounded-full bg-primary text-primary-foreground font-bold transition-transform duration-200 hover:scale-105">
              <a href="/downloads/queebi-mothers-day-coloring.pdf" target="_blank" rel="noopener noreferrer" onClick={() => trackDownload('mothers_day_card')}>
                <Download className="mr-2 h-5 w-5" /> {t("activities.downloadMothersDay")}
              </a>
            </Button>
          </div>
        </div>
      </AnimatedSection>

      <AnimatedSection className="bg-background py-20 md:py-32">
        <div className="container max-w-3xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold font-heading mb-6">{t("activities.howToUseTitle")}</h2>
          <p className="text-muted-foreground text-lg mb-10">{t("activities.howToUseDesc")}</p>
          <h2 className="text-2xl md:text-3xl font-bold font-heading mb-6">{t("activities.moreTitle")}</h2>
          <p className="text-muted-foreground text-lg mb-8">{t("activities.moreDesc")}</p>
          <p className="text-muted-foreground text-lg">
            <a href="https://www.instagram.com/queebi.land/" target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('instagram_activities')} className="font-bold text-foreground hover:text-accent underline">
              {t("activities.followInstagram")}
            </a>
          </p>
        </div>
      </AnimatedSection>

      <FaqGroupsSection title={t("faq.title")} groups={activitiesGroups} />
    </>
  );
};

export default Activities;
